import React, { useState, useRef } from "react";
import { Box, Button, Text, TextInput, FormField, Spinner } from "grommet";
import { Add, Upload, Link } from "grommet-icons";
import { presentToastSuccess, presentToastErrorContent } from "@shared/Toast";
import { KnowledgeBaseService } from "../../../services";
import { useTranslation } from "react-i18next";

const ACCEPTED_MIME_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
  "image/png",
  "image/jpeg",
  "image/webp",
];

const ACCEPT_STRING = ".pdf,.docx,.txt,.png,.jpg,.jpeg,.webp";

export default function KnowledgeBaseUpload({ onUploaded, internal = false }) {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [urlValue, setUrlValue] = useState("");
  const [urlName, setUrlName] = useState("");
  const [isAddingUrl, setIsAddingUrl] = useState(false);
  const fileInputRef = useRef(null);

  // ── File upload ─────────────────────────────────────────────────────────────

  const handleFileSelect = (files) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
      presentToastErrorContent({
        response: {
          data: {
            message: t("knowledgeBase.unsupportedFile"),
          },
        },
      });
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      presentToastErrorContent({
        response: {
          data: { message: t("knowledgeBase.fileTooLarge") },
        },
      });
      return;
    }
    uploadFile(file);
  };

  const uploadFile = (file) => {
    setIsUploading(true);
    KnowledgeBaseService.uploadDocument(file, file.name, internal).then(
      () => {
        presentToastSuccess(t("knowledgeBase.uploadSuccess"));
        setIsUploading(false);
        if (onUploaded) onUploaded();
      },
      (error) => {
        presentToastErrorContent(error);
        setIsUploading(false);
      },
    );
  };

  // ── Drag-and-drop handlers (native, no external library) ────────────────────

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) handleFileSelect(files);
  };

  // ── URL add ─────────────────────────────────────────────────────────────────

  const handleAddUrl = () => {
    if (!urlValue.trim()) return;
    setIsAddingUrl(true);
    KnowledgeBaseService.addUrl(
      urlValue.trim(),
      urlName.trim() || undefined,
      internal,
    ).then(
      () => {
        presentToastSuccess(t("knowledgeBase.urlSuccess"));
        setUrlValue("");
        setUrlName("");
        setIsAddingUrl(false);
        if (onUploaded) onUploaded();
      },
      (error) => {
        presentToastErrorContent(error);
        setIsAddingUrl(false);
      },
    );
  };

  return (
    <Box gap="medium">
      {/* ── File Drop Zone ────────────────────────────────────────────────── */}
      <Box gap="xsmall">
        <Text weight="bold" size="small">
          {t("knowledgeBase.uploadTitle")}
        </Text>
        <Box
          fill="horizontal"
          border={{ color: isDragging ? "brand" : "dark-4", style: "dashed" }}
          round="small"
          pad="medium"
          align="center"
          justify="center"
          gap="small"
          background={isDragging ? "light-2" : "light-1"}
          style={{ cursor: "pointer", minHeight: 100 }}
          onClick={() => !isUploading && fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragEnter={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {isUploading ? (
            <Spinner />
          ) : (
            <>
              <Upload size="medium" color="dark-3" />
              <Text size="small" color="dark-4" textAlign="center">
                {t("knowledgeBase.uploadDrag")}
              </Text>
              <Text size="xsmall" color="dark-5">
                {t("knowledgeBase.uploadFormats")}
              </Text>
            </>
          )}
        </Box>
        <input
          type="file"
          accept={ACCEPT_STRING}
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={(e) => handleFileSelect(e.target.files)}
        />
      </Box>

      {/* ── URL Input ─────────────────────────────────────────────────────── */}
      <Box gap="xsmall">
        <Text weight="bold" size="small">
          {t("knowledgeBase.urlTitle")}
        </Text>
        <Box direction="row" gap="small" align="end" wrap>
          <Box flex={{ grow: 1, shrink: 1 }} basis="200px">
            <FormField label={t("knowledgeBase.urlLabel")} margin="none">
              <TextInput
                placeholder={t("knowledgeBase.urlPlaceholder")}
                value={urlValue}
                onChange={({ target: { value } }) => setUrlValue(value)}
                icon={<Link size="small" />}
              />
            </FormField>
          </Box>
          <Box flex={{ grow: 1, shrink: 1 }} basis="140px">
            <FormField label={t("knowledgeBase.urlNameLabel")} margin="none">
              <TextInput
                placeholder={t("knowledgeBase.urlNamePlaceholder")}
                value={urlName}
                onChange={({ target: { value } }) => setUrlName(value)}
              />
            </FormField>
          </Box>
          <Button
            icon={isAddingUrl ? <Spinner size="small" /> : <Add size="small" />}
            label={t("knowledgeBase.addUrl")}
            primary
            color="brand"
            size="small"
            disabled={!urlValue.trim() || isAddingUrl}
            onClick={handleAddUrl}
          />
        </Box>
      </Box>
    </Box>
  );
}
